import { createQueryKeys, inferQueryKeys } from '@lukemorales/query-key-factory';
import { AxiosResponse } from 'axios';

import {
  $httpRootApi,
  AnyErrorResponse,
  queryClient,
  UpdateCacheCallback,
  updateQueryCache,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@core/http-client';
import { SavedViewType } from '@shared/models/saved_view/savedView';
import { getQueriesAsSearch } from '@shared/utils/common';
import { serializeBodyWithDetailsToFormData } from '@shared/utils/formdata-serializer';

import {
  LettingTransactionActivitiesIndexPayload,
  LettingTransactionActivitiesIndexResponse,
  LettingTransactionAtlasValidationErrorResponse,
  LettingTransactionCreateCommentPayload,
  LettingTransactionDeleteFilePayload,
  LettingTransactionExpireLease,
  LettingTransactionPublishToAtlasPayload,
  LettingTransactionPublishToRadiusPayload,
  LettingTransactionRenameFilePayload,
  LettingTransactionReorderFilesPayload,
  LettingTransactionsBulkAssignTeamsPayload,
  LettingTransactionsBulkAssignUserPayload,
  LettingTransactionsBulkChangeStatusPayload,
  LettingTransactionsBulkChangeTypePayload,
  LettingTransactionsBulkDeletePayload,
  LettingTransactionsBulkPublishToAtlasPayload,
  LettingTransactionsBulkSetAccuracyPayload,
  LettingTransactionsBulkSetLeaseLengthPayload,
  LettingTransactionsBulkSetLeaseStartPayload,
  LettingTransactionsBulkSetPreLetPayload,
  LettingTransactionsBulkSetSectorsPayload,
  LettingTransactionsBulkSetSourcePayload,
  LettingTransactionsBulkUpdateTagsPayload,
  LettingTransactionsCreatePayload,
  LettingTransactionsListPayload,
  LettingTransactionsListResponse,
  LettingTransactionsShowItem,
  LettingTransactionsSpaceDetailsUpdatePayload,
  LettingTransactionsUpdatePayload,
  LettingTransactionUpdateAddressPayload,
  LettingTransactionValidatePublishPreCreateParams,
} from './transactionsLettingsApi.types';

export const lettingTransactionsQueryKeys = createQueryKeys('lettingTransactions', {
  all: null,
  activitiesIndex: (payload: LettingTransactionActivitiesIndexPayload) => ({
    queryKey: [payload.lettingTransactionId, payload.query],
    queryFn: async ({ signal }) =>
      (
        await $httpRootApi.get<LettingTransactionActivitiesIndexResponse>(
          `/activities/letting_transaction/${payload.lettingTransactionId}${getQueriesAsSearch(payload.query)}`,
          { signal }
        )
      ).data,
  }),
  list: (payload: LettingTransactionsListPayload) => ({
    queryKey: [payload.viewType || '', payload.query],
    queryFn: async ({ signal }) => {
      if (payload.viewType === SavedViewType.map) {
        return (
          await $httpRootApi.post<any, AxiosResponse<LettingTransactionsListResponse>>(
            `/transactions/lettings/map`,
            payload.query,
            { signal }
          )
        ).data;
      }

      // Default
      return (
        await $httpRootApi.get<LettingTransactionsListResponse>(
          `/transactions/lettings${getQueriesAsSearch(payload.query)}`,
          {
            signal,
          }
        )
      ).data;
    },
  }),
  show: (id: string) => ({
    queryKey: [id],
    queryFn: async () => {
      const response = await $httpRootApi.get<LettingTransactionsShowItem>(`/transactions/lettings/${id}`);
      const data = response.data;

      if (
        data?.publish_to_atlas?.updated_at &&
        data?.updated_at &&
        typeof data.publish_to_atlas.updated_at === 'string' &&
        typeof data.updated_at === 'string'
      ) {
        const transactionUpdated = new Date(data.updated_at).getTime();
        const atlasUpdated = new Date(data.publish_to_atlas.updated_at).getTime();

        if (atlasUpdated > transactionUpdated) {
          data.updated_at = data.publish_to_atlas.updated_at;
        }
      }

      return data;
    },
  }),
  validateRadius: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/transactions/lettings/${id}/validate_publish_to_radius`)).data,
  }),
});

export type lettingTransactionsQueryKeys = inferQueryKeys<typeof lettingTransactionsQueryKeys>;

// ---- Queries ----

export const useLettingTransactionsListQuery = (
  payload: LettingTransactionsListPayload,
  options?: UseQueryOptions<LettingTransactionsListResponse>
) =>
  useQuery<lettingTransactionsQueryKeys['list']['queryKey'], LettingTransactionsListResponse>({
    ...lettingTransactionsQueryKeys.list(payload),
    keepPreviousData: true,
    ...options,
  });

export const useLettingTransactionsShowQuery = (id: string, options?: UseQueryOptions<LettingTransactionsShowItem>) =>
  useQuery<lettingTransactionsQueryKeys['show']['queryKey'], LettingTransactionsShowItem>({
    ...lettingTransactionsQueryKeys.show(id),
    ...options,
  });

export const useLettingTransactionsValidateRadiusQuery = (
  id: string,
  options?: UseQueryOptions<any, AnyErrorResponse>
) =>
  useQuery<lettingTransactionsQueryKeys['validateRadius']['queryKey'], any, AnyErrorResponse>({
    ...lettingTransactionsQueryKeys.validateRadius(id),
    ...options,
  });

// ---- Mutations ----

export const useLettingTransactionsCreateMutation = (
  opts?: UseMutationOptions<LettingTransactionsCreatePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) => {
      const formData = serializeBodyWithDetailsToFormData(data.body);

      return (
        await $httpRootApi.post<FormData>(`/transactions/lettings`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      ).data;
    },
  });

export const useLettingTransactionsUpdateMutation = (
  opts?: UseMutationOptions<LettingTransactionsUpdatePayload, LettingTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (payload) =>
      (
        await $httpRootApi.put<LettingTransactionsUpdatePayload['body'], AxiosResponse<LettingTransactionsShowItem>>(
          `/transactions/lettings/${payload.id}`,
          payload.body
        )
      ).data,
  });

export const useLettingTransactionsSpaceDetailsUpdateMutation = (
  opts?: UseMutationOptions<LettingTransactionsSpaceDetailsUpdatePayload, LettingTransactionsShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (payload) =>
      (
        await $httpRootApi.put<
          LettingTransactionsSpaceDetailsUpdatePayload['body'],
          AxiosResponse<LettingTransactionsShowItem>
        >(`/transactions/lettings/${payload.id}/space-details`, payload.body)
      ).data,
  });

export const useLettingTransactionCreateCommentMutation = (
  opts?: UseMutationOptions<LettingTransactionCreateCommentPayload, LettingTransactionsShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<
          LettingTransactionCreateCommentPayload['body'],
          AxiosResponse<LettingTransactionsShowItem>
        >(`/transactions/lettings/${data.id}/comment`, data.body)
      ).data,
  });

export const useLettingTransactionUpdateAddressMutation = (
  opts?: UseMutationOptions<LettingTransactionUpdateAddressPayload, LettingTransactionsShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<
          LettingTransactionUpdateAddressPayload['body'],
          AxiosResponse<LettingTransactionsShowItem>
        >(`/transactions/lettings/${data.id}/address`, data.body)
      ).data,
  });

export const useLettingTransactionExpireLease = (
  opts?: UseMutationOptions<LettingTransactionExpireLease, LettingTransactionsShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<LettingTransactionExpireLease['body'], AxiosResponse<LettingTransactionsShowItem>>(
          `/transactions/lettings/${data.id}/expire`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionDelete = (opts?: UseMutationOptions<number, number>) =>
  useMutation({
    ...opts,
    mutationFn: async (id) => {
      await $httpRootApi.delete(`/transactions/lettings/${id}`);

      return id;
    },
  });

export const useLettingTransactionPublishToRadiusMutation = (
  opts?: UseMutationOptions<LettingTransactionPublishToRadiusPayload, LettingTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<
          LettingTransactionPublishToRadiusPayload['body'],
          AxiosResponse<LettingTransactionsShowItem>
        >(`transactions/lettings/${data.id}/publish_to_radius`, data.body)
      ).data,
  });

export const useLettingTransactionPublishToAtlasMutation = (
  opts?: UseMutationOptions<LettingTransactionPublishToAtlasPayload, LettingTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<
          LettingTransactionPublishToAtlasPayload['body'],
          AxiosResponse<LettingTransactionsShowItem>
        >(`transactions/lettings/${data.id}/publish_to_atlas`, data.body)
      ).data,
  });

export const useLettingTransactionUpdateAtlasMutation = (
  opts?: UseMutationOptions<LettingTransactionPublishToAtlasPayload, LettingTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<
          LettingTransactionPublishToAtlasPayload['body'],
          AxiosResponse<LettingTransactionsShowItem>
        >(`transactions/lettings/${data.id}/update_atlas`, data.body)
      ).data,
  });

export const useLettingTransactionUnpublishAtlasMutation = (
  opts?: UseMutationOptions<{ id: string }, LettingTransactionsShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<void, AxiosResponse<LettingTransactionsShowItem>>(
          `transactions/lettings/${data.id}/unpublish_atlas`
        )
      ).data,
  });

export const useLettingTransactionValidatePublishToAtlasQuery = (
  id: string | undefined,
  opts?: UseQueryOptions<LettingTransactionAtlasValidationErrorResponse, LettingTransactionAtlasValidationErrorResponse>
) =>
  useQuery({
    ...opts,
    enabled: opts?.enabled ?? false,
    retry: false,
    queryKey: ['lettingTransactions', id, 'validatePublishToAtlas'],
    queryFn: async () => {
      if (!id) {
        throw new Error('Transaction ID is required');
      }

      return (
        await $httpRootApi.get<LettingTransactionAtlasValidationErrorResponse>(
          `transactions/lettings/${id}/validate_publish_to_atlas`
        )
      ).data;
    },
  });

export const useValidateDisposalLettingPublishQuery = (
  params: LettingTransactionValidatePublishPreCreateParams,
  opts?: UseQueryOptions<LettingTransactionAtlasValidationErrorResponse, LettingTransactionAtlasValidationErrorResponse>
) =>
  useQuery({
    ...opts,
    enabled: opts?.enabled ?? false,
    retry: false,
    onError: () => {
      // suppress error notification
    },
    queryKey: ['lettingTransactions', 'validateDisposalPublish', params],
    queryFn: async () => {
      return (
        await $httpRootApi.get<LettingTransactionAtlasValidationErrorResponse>(
          `transactions/lettings/validate_publish_to_atlas${getQueriesAsSearch(params)}`
        )
      ).data;
    },
  });

export const useLettingTransactionReorderFilesMutation = (
  opts?: UseMutationOptions<LettingTransactionReorderFilesPayload>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionReorderFilesPayload['body']>(
          `/transactions/lettings/${data.id}/assets`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionDeleteFileMutation = (
  opts?: UseMutationOptions<LettingTransactionDeleteFilePayload>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<LettingTransactionDeleteFilePayload>(
          `/transactions/lettings/${data.id}/assets/${data.assetId}`
        )
      ).data,
  });

export const useLettingTransactionRenameFileMutation = (
  opts?: UseMutationOptions<LettingTransactionRenameFilePayload>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionRenameFilePayload['body']>(
          `/transactions/lettings/${data.id}/assets/${data.assetId}`,
          data.body
        )
      ).data,
  });

// ---- Bulk Actions ----

export const useLettingTransactionsBulkChangeStatusMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkChangeStatusPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkChangeStatusPayload['body'], any>(
          `/letting-transactions/bulk-change-status`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkUpdateTagsMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkUpdateTagsPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkUpdateTagsPayload['body'], any>(
          `/letting-transactions/bulk-update-tags`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkChangeTypeMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkChangeTypePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkChangeTypePayload['body'], any>(
          `/letting-transactions/bulk-change-type`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkSetPreLetMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkSetPreLetPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkSetPreLetPayload['body'], any>(
          `/letting-transactions/bulk-set-pre-let`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkSetSectorsMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkSetSectorsPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkSetSectorsPayload['body'], any>(
          `/letting-transactions/bulk-set-sectors`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkDeleteMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkDeletePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<LettingTransactionsBulkDeletePayload['body'], any>(`/letting-transactions/bulk`, {
          data: data.body,
        })
      ).data,
  });

export const useLettingTransactionsBulkAssignUserMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkAssignUserPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkAssignUserPayload['body'], any>(
          `/letting-transactions/bulk-assign-user`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkAssignTeamsMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkAssignTeamsPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkAssignTeamsPayload['body'], any>(
          `/letting-transactions/bulk-assign-teams`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkSetLeaseStartMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkSetLeaseStartPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkSetLeaseStartPayload['body'], any>(
          `/letting-transactions/bulk-set-lease-start`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkSetLeaseLengthMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkSetLeaseLengthPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkSetLeaseLengthPayload['body'], any>(
          `/letting-transactions/bulk-set-lease-length`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkSetAccuracyMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkSetAccuracyPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkSetAccuracyPayload['body'], any>(
          `/letting-transactions/bulk-set-accuracy`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkSetSourceMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkSetSourcePayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkSetSourcePayload['body'], any>(
          `/letting-transactions/bulk-set-source`,
          data.body
        )
      ).data,
  });

export const useLettingTransactionsBulkPublishToAtlasMutation = (
  opts?: UseMutationOptions<LettingTransactionsBulkPublishToAtlasPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<LettingTransactionsBulkPublishToAtlasPayload['body'], any>(
          `/letting-transactions/bulk-publish-to-atlas`,
          data.body
        )
      ).data,
  });

// ---- Utils ----

export const updateLettingTransactionShowCache = (
  transactionId: string,
  callback: UpdateCacheCallback<LettingTransactionsShowItem>
) => updateQueryCache<LettingTransactionsShowItem>(lettingTransactionsQueryKeys.show(transactionId).queryKey, callback);

// Specialized cache update with field-level caching for Atlas ranking fields
export const updateLettingTransactionShowCacheWithFields = (
  transactionId: string,
  update: Partial<LettingTransactionsShowItem>
) =>
  updateQueryCache<LettingTransactionsShowItem>(
    lettingTransactionsQueryKeys.show(transactionId).queryKey,
    (transaction) => {
      // Atlas ranking fields - similar pattern to sales size_value fix
      if ('lease_length_years' in update) {
        transaction.lease_length_years =
          update.lease_length_years !== undefined ? update.lease_length_years : transaction.lease_length_years;
      }

      if ('lease_length_months' in update) {
        transaction.lease_length_months =
          update.lease_length_months !== undefined ? update.lease_length_months : transaction.lease_length_months;
      }

      if ('lease_length_days' in update) {
        transaction.lease_length_days =
          update.lease_length_days !== undefined ? update.lease_length_days : transaction.lease_length_days;
      }

      if ('rent_value' in update) {
        transaction.rent_value = update.rent_value !== undefined ? update.rent_value : transaction.rent_value;
      }

      if ('rent_metric' in update) {
        transaction.rent_metric = update.rent_metric ?? transaction.rent_metric;
      }

      if ('size_value' in update) {
        transaction.size_value = update.size_value !== undefined ? update.size_value : transaction.size_value;
      }

      if ('size_metric' in update) {
        transaction.size_metric = update.size_metric ?? transaction.size_metric;
      }

      if ('lease_start' in update) {
        transaction.lease_start = update.lease_start ?? transaction.lease_start;
      }

      if ('lease_end' in update) {
        transaction.lease_end = update.lease_end ?? transaction.lease_end;
      }

      if ('type' in update) {
        transaction.type = update.type !== undefined ? update.type : transaction.type;
      }

      // Mandatory fields
      if ('building_types' in update) {
        transaction.building_types = update.building_types ?? transaction.building_types;
      }

      if ('tenant_agent_users' in update) {
        transaction.tenant_agent_users = update.tenant_agent_users ?? transaction.tenant_agent_users;
      }

      if ('landlord_agent_users' in update) {
        transaction.landlord_agent_users = update.landlord_agent_users ?? transaction.landlord_agent_users;
      }

      if ('our_acquisition' in update) {
        transaction.our_acquisition = update.our_acquisition ?? transaction.our_acquisition;
      }

      if ('our_disposal' in update) {
        transaction.our_disposal = update.our_disposal ?? transaction.our_disposal;
      }

      return transaction;
    }
  );

export const invalidateLettingTransactionsListQueries = () => {
  queryClient.invalidateQueries({
    queryKey: lettingTransactionsQueryKeys.list._def,
    exact: false,
  });
};

export const invalidateLettingTransactionShowQuery = (transactionId: string) => {
  queryClient.invalidateQueries({
    queryKey: lettingTransactionsQueryKeys.show(transactionId).queryKey,
    exact: true,
  });
};
